/**
 * Reglas del listado de pedidos. La RPC aplica las mismas condiciones;
 * aquí están para poder probarlas sin base de datos.
 *
 * El checkout de la tienda no fija `expires_at`. Stripe cierra la sesión
 * a las 24 h, así que un pendiente de pago más antiguo es un abandono.
 */

export const CHECKOUT_OPEN_MS = 24 * 60 * 60 * 1000;

export const ORDER_VIEWS = [
    'to_ship',
    'pending_payment',
    'abandoned',
    'shipped',
    'completed',
    'closed',
    'all',
] as const;

export type OrderViewId = (typeof ORDER_VIEWS)[number];

export const ORDER_VIEW_LABELS: Record<OrderViewId, string> = {
    to_ship: 'Por enviar',
    pending_payment: 'Pendientes de pago',
    abandoned: 'Checkouts abandonados',
    shipped: 'Enviados',
    completed: 'Completados',
    closed: 'Cancelados y reembolsados',
    all: 'Todos',
};

export const ORDER_PAGE_SIZE = 50;
export const ORDER_SELECT_CAP = 2000;
export const ORDER_FILTER_KEYS = ['origin', 'from', 'to', 'min'] as const;

export const ORDER_INDEX_URL = {
    views: ORDER_VIEWS,
    defaultView: 'to_ship' as const,
    filterKeys: ORDER_FILTER_KEYS,
};

export interface OriginLine {
    catalogProductId: string | number | null;
    supplierSlug?: string | null;
    supplierName?: string | null;
}

export interface OrderOrigin {
    key: string;
    label: string;
}

/** Merch AiPeces, el nombre del único proveedor, o Mixto. Sin líneas: «—». */
export function orderOrigin(lines: readonly OriginLine[]): OrderOrigin {
    if (lines.length === 0) return { key: 'none', label: '—' };
    const merch = lines.filter((line) => line.catalogProductId == null);
    if (merch.length === lines.length) return { key: 'merch', label: 'Merch AiPeces' };
    const catalog = lines.filter((line) => line.catalogProductId != null);
    const slugs = new Set(catalog.map((line) => line.supplierSlug || ''));
    if (merch.length === 0 && slugs.size === 1) {
        const slug = catalog[0]?.supplierSlug || 'proveedor';
        const name = catalog.find((line) => line.supplierName)?.supplierName?.trim();
        return { key: slug, label: name || slug };
    }
    return { key: 'mixed', label: 'Mixto' };
}

/**
 * El listado de un proveedor incluye los pedidos mixtos que tengan líneas suyas.
 * «mixed» sigue siendo solo la etiqueta Mixto.
 */
export function orderIncludesOrigin(lines: readonly OriginLine[], origin: string): boolean {
    const key = origin.trim();
    if (!key) return true;
    const label = orderOrigin(lines);
    if (key === 'mixed' || key === 'none') return label.key === key;
    if (key === 'merch') return lines.some((line) => line.catalogProductId == null);
    return lines.some((line) => line.catalogProductId != null && (line.supplierSlug || '') === key);
}

/** Días desde el pago en un pedido pagado o en preparación que aún no ha salido. */
export function waitDays(
    paidAt: string | null | undefined,
    status: string,
    shippedAt: string | null | undefined,
    now: Date,
): number | null {
    if (status !== 'paid' && status !== 'processing') return null;
    if (shippedAt) return null;
    if (!paidAt) return null;
    const start = new Date(paidAt);
    if (Number.isNaN(start.getTime())) return null;
    const elapsed = now.getTime() - start.getTime();
    if (elapsed < 0) return 0;
    return Math.floor(elapsed / 86_400_000);
}

export type WaitTone = 'neutro' | 'aviso' | 'critico';

export function waitTone(days: number | null): WaitTone | null {
    if (days == null) return null;
    if (days >= 7) return 'critico';
    if (days >= 3) return 'aviso';
    return 'neutro';
}

export function waitLabel(days: number | null): string {
    if (days == null) return '—';
    return `${days} d`;
}

export interface ViewOrder {
    status: string;
    createdAt: string;
}

export function isAbandonedCheckout(createdAt: string, now: Date): boolean {
    const created = new Date(createdAt);
    if (Number.isNaN(created.getTime())) return false;
    return now.getTime() - created.getTime() >= CHECKOUT_OPEN_MS;
}

/** Los últimos pedidos de un proveedor no mezclan checkouts abandonados. */
export function recentSupplierOrders<T extends { status: string; created_at: string }>(
    rows: readonly T[],
    now: Date,
    limit = 5,
): T[] {
    return rows
        .filter((row) => !orderInView(
            { status: row.status, createdAt: row.created_at },
            'abandoned',
            now,
        ))
        .slice(0, limit);
}

/**
 * Enviados incluye `delivered`: si no, un pedido entregado solo se vería en Todos.
 * Los BORR- no se tratan aparte: todo pendiente de pago lleva ese número.
 */
export function orderInView(order: ViewOrder, view: OrderViewId, now: Date): boolean {
    const abandoned = order.status === 'pending_payment' && isAbandonedCheckout(order.createdAt, now);
    switch (view) {
        case 'all':
            return true;
        case 'to_ship':
            return order.status === 'paid' || order.status === 'processing';
        case 'pending_payment':
            return order.status === 'pending_payment' && !abandoned;
        case 'abandoned':
            return abandoned;
        case 'shipped':
            return order.status === 'shipped' || order.status === 'delivered';
        case 'completed':
            return order.status === 'completed';
        case 'closed':
            return order.status === 'cancelled' || order.status === 'refunded';
        default:
            return false;
    }
}

export const COMPLETABLE_STATUSES = new Set(['paid', 'processing', 'shipped']);
export const CANCELLABLE_STATUSES = new Set(['pending_payment']);

export function idsWithStatus(
    rows: readonly { id: string; status: string }[],
    selected: readonly string[],
    allowed: ReadonlySet<string>,
): string[] {
    const picked = new Set(selected);
    return rows.filter((row) => picked.has(row.id) && allowed.has(row.status)).map((row) => row.id);
}

export function defaultOrderSort(view: string): { sort: string; dir: SortDir } {
    if (view === 'to_ship') return { sort: 'paid_at', dir: 'asc' };
    return { sort: 'created_at', dir: 'desc' };
}

type SortDir = 'asc' | 'desc';

export const ORDER_CSV_HEADERS = [
    'Pedido',
    'Fecha',
    'Cliente',
    'Origen',
    'Artículos',
    'Total',
    'Estado',
    'Espera',
    'Factura',
] as const;

export { csvCell, rowsToCsv } from '@/lib/csv';

export function isIsoDate(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** El filtro `min` de la URL guarda céntimos enteros. */
export function centsFromFilter(raw: string | undefined): number | null {
    if (!raw) return null;
    if (!/^\d+$/.test(raw)) return null;
    const cents = Number(raw);
    return Number.isSafeInteger(cents) ? cents : null;
}
